import { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  useSurveyorsInfinite,
  useCreateSurveyor,
  useAssignSurveyor,
  useUpdateSurveyStatus,
  useSurveyAssignments,
} from "../../../hooks/useWorkshopQueries";
import {
  Badge, PageHeader, Spinner, SearchInput, EmptyState, ErrorState,
  MetricCard, Modal, InfoRow, Avatar,
  formatDate, formatDateTime,
} from "../../../Component/Workshop/UI/index";

import {PlusIcon, EditIcon, GridIcon, ListIcon} from "../../../Component/Workshop/UI/icons";

// ─── Constants ────────────────────────────────────────────────────────────────
const SURVEY_TYPES = [
  { value:"SPOT_SURVEY",          label:"Spot Survey"          },
  { value:"WORKSHOP_SURVEY",      label:"Workshop Survey"      },
  { value:"SECOND_SURVEY",        label:"Second Survey"        },
  { value:"FINAL_SURVEY",         label:"Final Survey"         },
  { value:"REINSPECTION",         label:"Reinspection"         },
];

const SURVEY_STATUS_CFG = {
  ASSIGNED:   { label:"Assigned",   variant:"blue"   },
  ACCEPTED:   { label:"Accepted",   variant:"teal"   },
  REJECTED:   { label:"Rejected",   variant:"red"    },
  RESCHEDULED:{ label:"Rescheduled",variant:"yellow" },
  COMPLETED:  { label:"Completed",  variant:"green"  },
  CANCELLED:  { label:"Cancelled",  variant:"gray"   },
};

const SURVEY_TABS = [
  { key:"ACTIVE",    label:"Active"    },
  { key:"COMPLETED", label:"Completed" },
  { key:"REJECTED",  label:"Rejected"  },
  { key:"ALL",       label:"All"       },
];

const SPEC_LABELS = {
  SPOT_SURVEY:"Spot Survey", WORKSHOP_SURVEY:"Workshop Survey",
  SECOND_SURVEY:"Second Survey", FINAL_SURVEY:"Final Survey", REINSPECTION:"Reinspection",
};

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function SurveyorsPage() {
  const navigate = useNavigate();
    const location = useLocation(); 
    const [searchParams, setSearchParams] = useSearchParams();
    const globalSearch = searchParams.get("q") || "";

  const prefillCaseId = location.state?.prefillCaseId ?? ""; 
  const returnTo      = location.state?.returnTo ?? null;        
  const returnTab     = location.state?.returnTab ?? "Insurance";   

  // Main section tab — Surveyor Directory | Survey Assignments
  const [mainTab,     setMainTab]     = useState("directory");

  // Surveyor directory filters + pagination
  const [svSearch, setSvSearch] = useState(globalSearch);
  const [debouncedSvSearch, setDebouncedSvSearch] = useState("");
  const [cityFilter,  setCityFilter]  = useState("");
  const [insFilter,   setInsFilter]   = useState("");
  const [svViewMode,  setSvViewMode]  = useState("grid");

  // Survey assignments filters
  const [surveyTab,   setSurveyTab]   = useState("ACTIVE");
  const [surveySearch, setSurveySearch] = useState(globalSearch);
  const [debouncedSurveySearch, setDebouncedSurveySearch] = useState("");

  const setGlobalQuery = (value) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value?.trim()) next.set("q", value);
        else next.delete("q");
        return next;
      },
      { replace: true }
    );
  };

  const handleDirectorySearchChange = (value) => {
    setSvSearch(value);
    setGlobalQuery(value);
  };

  const handleAssignmentsSearchChange = (value) => {
    setSurveySearch(value);
    setGlobalQuery(value);
  };

  // Modals
  const [addModal,    setAddModal]    = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [updateModal, setUpdateModal] = useState(null);
  const [detailSv,    setDetailSv]    = useState(null);

  // Form states
  const [addForm,    setAddForm]    = useState(emptyAddForm());
  const [assignForm, setAssignForm] = useState({ caseId: prefillCaseId, surveyType:"", scheduledDate:"", notes:"" });
  const [updateForm, setUpdateForm] = useState({ status:"", remarks:"", rejectionReason:"", surveyCompletedDate:"" });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSvSearch(svSearch), 500);
    return () => clearTimeout(t);
  }, [svSearch]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSurveySearch(surveySearch), 500);
    return () => clearTimeout(t);
  }, [surveySearch]);

  useEffect(() => {
    if (mainTab === "directory") {
      if (globalSearch !== svSearch) setSvSearch(globalSearch);
      return;
    }
    if (globalSearch !== surveySearch) setSurveySearch(globalSearch);
  }, [globalSearch, mainTab]);
  // ── Surveyor directory — infinite / paginated ────────────────────────────
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: svLoading,
    isError:   svError,
  } = useSurveyorsInfinite({ search: debouncedSvSearch, city: cityFilter, insurance: insFilter });
 
  const surveyors = data?.pages.flatMap(p => p.data) ?? [];
  const svTotal   = data?.pages[0]?.total ?? 0;
  
   // ── Survey assignments ───────────────────────────────────────────────────
  const {
    data:      surveyData,
    isLoading: surveyLoading,
  } = useSurveyAssignments({ tab: surveyTab, search: debouncedSurveySearch  });
 
  const surveys    = surveyData?.surveys  ?? [];
  const tabCounts  = surveyData?.counts   ?? { ACTIVE:0, COMPLETED:0, REJECTED:0, ALL:0 };
  const surveyMetrics = surveyData?.metrics ?? {};
  const metrics    = {
    totalSurveyors: svTotal,
    totalSurveys:   surveyMetrics.totalSurveys ?? tabCounts.ALL,
    active:         surveyMetrics.active ?? tabCounts.ACTIVE,
    completed:      surveyMetrics.completed ?? tabCounts.COMPLETED,
    rejected:       surveyMetrics.rejected ?? tabCounts.REJECTED,
  };
  // ── Mutations ────────────────────────────────────────────────────────────
  const createSurveyor = useCreateSurveyor();
  const assignSurveyor = useAssignSurveyor();
  const updateSurveyStatus = useUpdateSurveyStatus();

  // ── Handlers ────────────────────────────────────────────────────────────
  const handleAddSurveyor = async () => {
    // ── Real API call — uncomment when backend ready ───────────────────────
    await createSurveyor.mutateAsync({
      name:            addForm.name,
      phone:           addForm.phone,
      email:           addForm.email           || null,
      insuranceCompany:addForm.insuranceCompany,
      city:            addForm.city            || null,
      state:           addForm.state           || null,
      licenseNo:       addForm.licenseNo       || null,
      licenseExpiry:   addForm.licenseExpiry   || null,
      specialization:  addForm.specialization,
    });
    console.log("Add surveyor:", addForm);
    setAddModal(false);
    setAddForm(emptyAddForm());
  };

  const handleAssign = async () => {
    if (!assignForm.caseId.trim() || !assignForm.surveyType) return;
    // ── Real API call — uncomment when backend ready ─────────────────────
    await assignSurveyor.mutateAsync({
      surveyorId:    assignModal.surveyorCode || assignModal.id,
      surveyorName:  assignModal.name || null,
      caseId:        assignForm.caseId,
      surveyType:    assignForm.surveyType,
      scheduledDate: assignForm.scheduledDate || null,
      notes:         assignForm.notes         || null,
    });
    console.log("Assign:", assignModal?.id, assignForm);
    setAssignModal(null);
    setAssignForm({ caseId:"", surveyType:"", scheduledDate:"", notes:"" });

    if (returnTo) {
    navigate(returnTo, { state: { activeTab: returnTab } });
  }
  
  };

  const openUpdate = (survey) => {
    setUpdateModal(survey);
    setUpdateForm({
      status:              survey.surveyStatus,
      remarks:             survey.remarks || "",
      rejectionReason:     survey.rejectionReason || "",
      surveyCompletedDate: survey.surveyCompletedDate ? survey.surveyCompletedDate.slice(0,16) : "",
    });
  };

  const handleUpdateSurvey = async () => {
    if (!updateForm.status) return;
    await updateSurveyStatus.mutateAsync({
      surveyId:            updateModal.id,
      status:              updateForm.status,
      remarks:             updateForm.remarks             || null,
      rejectionReason:     updateForm.rejectionReason     || null,
      surveyCompletedDate: updateForm.surveyCompletedDate || null,
    });
    setUpdateModal(null);
    setUpdateForm({ status:"", remarks:"", rejectionReason:"", surveyCompletedDate:"" });
  };

 return (
    <div className="wd-animate" style={{ display:"flex", flexDirection:"column", gap:20 }}>
 
      {/* Header */}
      <PageHeader
        title="Surveyors"
        subtitle="Manage surveyors from insurance companies and track all survey assignments"
        actions={
          <button className="wd-btn wd-btn-primary" onClick={() => setAddModal(true)}>
            <PlusIcon /> Add Surveyor
          </button>
        }
      />
 
      {/* Metrics */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(5,1fr)", gap:12 }}>
        <MetricCard label="Total Surveyors" value={metrics.totalSurveyors} color="#3b82f6" />
        <MetricCard label="Total Surveys"   value={metrics.totalSurveys}   color="#a855f7" />
        <MetricCard label="Active Surveys"  value={metrics.active}         color="#f97316" />
        <MetricCard label="Completed"       value={metrics.completed}      color="#22c55e" />
        <MetricCard label="Rejected"        value={metrics.rejected}       color="#ef4444" />
      </div>
 
      {/* ── Main tab switcher ── */}
      <div style={{ display:"flex", borderBottom:"1px solid var(--wd-border)" }}>
        <button
          className={`wd-detail-tab ${mainTab === "directory" ? "active" : ""}`}
          onClick={() => setMainTab("directory")}
          style={{ display:"flex", alignItems:"center", gap:7 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
            <circle cx="9" cy="7" r="4"/>
            <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
          </svg>
          Surveyor Directory
          <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10, background:"rgba(255,255,255,.1)", marginLeft:2 }}>
            {svTotal}
          </span>
        </button>
        <button
          className={`wd-detail-tab ${mainTab === "assignments" ? "active" : ""}`}
          onClick={() => setMainTab("assignments")}
          style={{ display:"flex", alignItems:"center", gap:7 }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
          </svg>
          Survey Assignments
          <span style={{ fontSize:10, fontWeight:700, padding:"1px 6px", borderRadius:10, background:"rgba(255,255,255,.1)", marginLeft:2 }}>
            {tabCounts.ALL}
          </span>
        </button>
      </div>
 
      {/* ════════ TAB: SURVEYOR DIRECTORY ════════ */}
      {mainTab === "directory" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
 
          {/* Filters */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
            <div style={{ fontSize:13, color:"var(--wd-t3)" }}>
              Showing <strong style={{ color:"var(--wd-t1)" }}>{surveyors.length}</strong>
              {svTotal > surveyors.length
                ? <> of <strong style={{ color:"var(--wd-t1)" }}>{svTotal}</strong> surveyors</>
                : <> surveyor{surveyors.length !== 1 ? "s" : ""}</>
              }
              {(svSearch || cityFilter || insFilter) && (
                <button
                  className="wd-btn wd-btn-ghost wd-btn-sm"
                  style={{ marginLeft:8, fontSize:11 }}
                  onClick={() => { handleDirectorySearchChange(""); setCityFilter(""); setInsFilter(""); }}
                >
                  ✕ Clear filters
                </button>
              )}
            </div>
            <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap" }}>
              <SearchInput
                value={svSearch}
                onChange={handleDirectorySearchChange}
                placeholder="Name, phone, email, ID..."
              />
              <input className="wd-input" style={{ width:110 }} placeholder="City..."
                value={cityFilter} onChange={e => setCityFilter(e.target.value)} />
              <input className="wd-input" style={{ width:150 }} placeholder="Insurance co..."
                value={insFilter} onChange={e => setInsFilter(e.target.value)} />
              <div style={{ display:"flex", gap:2 }}>
                <button
                  className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
                  style={svViewMode === "grid" ? { background:"var(--wd-hover)" } : {}}
                  onClick={() => setSvViewMode("grid")}
                >
                  <GridIcon />
                </button>
                <button
                  className="wd-btn wd-btn-ghost wd-btn-icon wd-btn-sm"
                  style={svViewMode === "table" ? { background:"var(--wd-hover)" } : {}}
                  onClick={() => setSvViewMode("table")}
                >
                  <ListIcon />
                </button>
              </div>
            </div>
          </div>
 
          {/* Content */}
          {svLoading ? <Spinner /> :
            svError   ? <ErrorState message="Failed to load surveyors" onRetry={() => {}} /> :
            surveyors.length === 0 ? (
              <div className="wd-card" style={{ padding:32 }}>
                <EmptyState message="No surveyors found" />
              </div>
            ) : (
              <>
                {svViewMode === "grid"
                  ? <SurveyorGrid  surveyors={surveyors} onAssign={setAssignModal} onDetail={setDetailSv} />
                  : <SurveyorTable surveyors={surveyors} onAssign={setAssignModal} onDetail={setDetailSv} />
                }
 
                {/* Load More */}
                {hasNextPage && (
                  <div style={{ display:"flex", justifyContent:"center" }}>
                    <button
                      className="wd-btn wd-btn-ghost"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      style={{ minWidth:220, justifyContent:"center" }}
                    >
                      {isFetchingNextPage ? "Loading..." : `Load more surveyors · ${svTotal - surveyors.length} remaining`}
                    </button>
                  </div>
                )}
              </>
            )
          }
        </div>
      )}
 
      {/* ════════ TAB: SURVEY ASSIGNMENTS ════════ */}
      {mainTab === "assignments" && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
 
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
            {/* Status tabs */}
            <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
              {SURVEY_TABS.map(t => (
                <button key={t.key}
                  className={`wd-filter-tab ${surveyTab === t.key ? "active" : ""}`}
                  onClick={() => setSurveyTab(t.key)}
                >
                  {t.label}
                  <span style={{ marginLeft:5, fontSize:10, fontWeight:700, padding:"1px 5px", borderRadius:10, background:"rgba(255,255,255,.12)" }}>
                    {tabCounts[t.key]}
                  </span>
                </button>
              ))}
            </div>
            <SearchInput value={surveySearch} onChange={handleAssignmentsSearchChange} placeholder="Case no, surveyor, vehicle..." />
          </div>
 
            {surveyLoading ? <Spinner /> :
            surveys.length === 0
              ? <div className="wd-card" style={{ padding:32 }}><EmptyState message={`No ${surveyTab.toLowerCase()} surveys`} /></div>
              : <SurveyTable surveys={surveys} onUpdate={openUpdate} onNavigate={navigate} />
            }
        </div>
      )}
 
      {/* Add Surveyor Modal */}
      <AddSurveyorModal
        open={addModal}
        form={addForm} setForm={setAddForm}
        onClose={() => { setAddModal(false); setAddForm(emptyAddForm()); }}
        onSave={handleAddSurveyor}
        loading={createSurveyor.isPending}
      />
 
      {/* Assign to Case Modal */}
      <AssignModal
        sv={assignModal}
        form={assignForm} setForm={setAssignForm}
        onClose={() => setAssignModal(null)}
        onAssign={handleAssign}
        loading={assignSurveyor.isPending}
      />
 
      {/* Update Survey Status Modal */}
      <UpdateSurveyModal
        survey={updateModal}
        form={updateForm} setForm={setUpdateForm}
        onClose={() => setUpdateModal(null)}
        onSave={handleUpdateSurvey}
        loading={updateSurveyStatus.isPending}
      />
 
      {/* Surveyor Detail Modal */}
      <SurveyorDetailModal
        sv={detailSv}
        surveys={surveys}
        onClose={() => setDetailSv(null)}
        onAssign={() => { setAssignModal(detailSv); setDetailSv(null); }}
        onNavigate={navigate}
      />
    </div>
  );
}

// ─── Surveyor Grid ────────────────────────────────────────────────────────────
function SurveyorGrid({ surveyors, onAssign, onDetail }) {
  return (
    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px, 1fr))", gap:14 }}>
      {surveyors.map(sv => {
        const licExpiring = new Date(sv.licenseExpiry) < new Date(Date.now() + 90*86400000);
        return (
          <div key={sv.id} className="wd-card"
            style={{ padding:18, cursor:"pointer", transition:"border-color .15s" }}
            onClick={() => onDetail(sv)}
            onMouseEnter={e => e.currentTarget.style.borderColor = "var(--wd-blue)"}
            onMouseLeave={e => e.currentTarget.style.borderColor = "var(--wd-border)"}
          >
            <div style={{ display:"flex", gap:12, alignItems:"flex-start", marginBottom:12 }}>
              <Avatar name={sv.name} size={42} />
              <div style={{ flex:1 }}>
                <div style={{ fontWeight:700, fontSize:14, color:"var(--wd-t1)" }}>{sv.name}</div>
                <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:1 }}>📍 {sv.city}, {sv.state}</div>
                <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:1 }}>🏢 {sv.insuranceCompany}</div>
              </div>
              <Badge variant="green">ACTIVE</Badge>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:10 }}>
              <MiniStat label="Total Surveys" value={sv.totalSurveys} />
              <MiniStat label="License Exp"   value={formatDate(sv.licenseExpiry)}
                color={licExpiring ? "var(--wd-red)" : "var(--wd-t1)"} />
            </div>

            <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:10 }}>
              {sv.specialization.map(s => (
                <span key={s} className="wd-badge wd-badge-purple" style={{ fontSize:10 }}>
                  {SPEC_LABELS[s] || s}
                </span>
              ))}
            </div>

            {licExpiring && (
              <div style={{ fontSize:11, color:"var(--wd-red)", padding:"4px 8px", background:"rgba(239,68,68,.08)", borderRadius:6, marginBottom:10 }}>
                ⚠️ License expires {formatDate(sv.licenseExpiry)}
              </div>
            )}

            <div className="wd-divider" style={{ margin:"0 0 10px" }} />
            <div style={{ display:"flex", gap:6 }} onClick={e => e.stopPropagation()}>
              <button className="wd-btn wd-btn-ghost wd-btn-sm" style={{ flex:1, justifyContent:"center" }}
                onClick={() => onDetail(sv)}>View</button>
              <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => onAssign(sv)}>
                Assign to Case
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Surveyor Table ───────────────────────────────────────────────────────────
function SurveyorTable({ surveyors, onAssign, onDetail }) {
  return (
    <div className="wd-card">
      <div className="wd-table-wrap">
        <table className="wd-table">
          <thead>
            <tr>
              <th>SURVEYOR</th><th>INSURANCE COMPANY</th><th>LOCATION</th>
              <th>CATEGORY</th><th>SURVEYS</th><th>LICENSE EXPIRY</th><th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {surveyors.map(sv => {
              const licExpiring = new Date(sv.licenseExpiry) < new Date(Date.now() + 90*86400000);
              return (
                <tr key={sv.id} onClick={() => onDetail(sv)}>
                  <td>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <Avatar name={sv.name} size={30} />
                      <div>
                        <div style={{ fontWeight:600, fontSize:13 }}>{sv.name}</div>
                        <div style={{ fontSize:11, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)" }}>{sv.phone}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ fontSize:13, color:"var(--wd-t2)" }}>{sv.insuranceCompany}</td>
                  <td>
                    <div style={{ fontSize:13 }}>{sv.city}</div>
                    <div style={{ fontSize:11, color:"var(--wd-t3)" }}>{sv.state}</div>
                  </td>
                  <td>
                    <div style={{ fontSize:13 }}>{sv.category}</div>
                  </td>
                  <td style={{ fontWeight:600 }}>{sv.totalSurveys}</td>
                  <td>
                    <span style={{ fontSize:13, color: licExpiring ? "var(--wd-red)" : "var(--wd-t2)", fontWeight: licExpiring ? 600 : 400 }}>
                      {formatDate(sv.licenseExpiry)}{licExpiring && " ⚠️"}
                    </span>
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display:"flex", gap:5 }}>
                      <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => onDetail(sv)}>View</button>
                      <button className="wd-btn wd-btn-primary wd-btn-sm" onClick={() => onAssign(sv)}>Assign</button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Survey Assignments Table ─────────────────────────────────────────────────
function SurveyTable({ surveys, onUpdate, onNavigate }) {
  return (
    <div className="wd-card">
      <div className="wd-table-wrap">
        <table className="wd-table">
          <thead>
            <tr>
              <th>CASE</th><th>SURVEYOR</th><th>TYPE</th>
              <th>ASSIGNED DATE</th><th>SCHEDULED DATE</th><th>COMPLETED DATE</th>
              <th>STATUS</th><th>ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {surveys.map(s => {
              const cfg = SURVEY_STATUS_CFG[s.surveyStatus] || { label:s.surveyStatus, variant:"gray" };
              return (
                <tr key={s.id}>
                  <td>
                    <button className="wd-btn wd-btn-ghost wd-btn-sm"
                      style={{ fontFamily:"var(--wd-mono)", fontSize:12, color:"var(--wd-blue)", padding:"2px 6px" }}
                      onClick={() => onNavigate(`/admin/cases/${s.caseId}`)}>
                      {s.caseId}
                    </button>
                    <div style={{ fontSize:11, color:"var(--wd-t3)", fontFamily:"var(--wd-mono)", marginTop:1 }}>
                      {s.vehicleNo}
                    </div>
                  </td>

                  <td style={{ fontSize:13, fontWeight:500 }}>{s.surveyorName}</td>

                  <td>
                    <span className="wd-badge wd-badge-purple" style={{ fontSize:10 }}>
                      {SPEC_LABELS[s.surveyType] || s.surveyType}
                    </span>
                    <div style={{ fontSize:10, color:"var(--wd-t3)", marginTop:2 }}>Seq #{s.surveySequence}</div>
                  </td>

                  <td style={{ fontSize:12, color:"var(--wd-t3)" }}>
                    {formatDateTime(s.assignedAt)}
                  </td>

                  <td style={{ fontSize:12, color:"var(--wd-t2)" }}>
                    {s.scheduledDate ? formatDateTime(s.scheduledDate) : <span style={{ color:"var(--wd-t3)" }}>—</span>}
                  </td>

                  <td>
                    {s.surveyCompletedDate
                      ? <span style={{ fontSize:12, color:"var(--wd-green)", fontWeight:500 }}>
                          {formatDateTime(s.surveyCompletedDate)}
                        </span>
                      : <span style={{ fontSize:12, color:"var(--wd-t3)" }}>—</span>
                    }
                  </td>

                  <td>
                    <Badge variant={cfg.variant}>{cfg.label}</Badge>
                    {s.rejectionReason && (
                      <div style={{ fontSize:10, color:"var(--wd-red)", marginTop:3, maxWidth:130, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                        title={s.rejectionReason}>
                        ✗ {s.rejectionReason}
                      </div>
                    )}
                    {s.remarks && s.surveyStatus === "COMPLETED" && (
                      <div style={{ fontSize:10, color:"var(--wd-t3)", marginTop:2, maxWidth:130, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
                        title={s.remarks}>
                        📋 {s.remarks}
                      </div>
                    )}
                  </td>

                  <td>
                    <button className="wd-btn wd-btn-ghost wd-btn-sm" onClick={() => onUpdate(s)}>
                      <EditIcon /> Update
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Add Surveyor Modal ───────────────────────────────────────────────────────
// Admin enters details received from insurance company via email
function AddSurveyorModal({ open, form, setForm, onClose, onSave,loading }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleSpec = spec => setForm(f => ({
    ...f,
    specialization: f.specialization.includes(spec)
      ? f.specialization.filter(s => s !== spec)
      : [...f.specialization, spec],
  }));

  return (
    <Modal open={open} onClose={onClose} title="Add Surveyor" maxWidth={580}
      footer={<>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="wd-btn wd-btn-primary"
          disabled={!form.name.trim() || !form.phone.trim() || !form.insuranceCompany.trim()}
          onClick={onSave}>
          {loading ? "Saving..." : "Add Surveyor"}
        </button>
      </>}
    >
      {/* Source note */}
      <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)" }}>
        <span>ℹ️</span>
        <span style={{ fontSize:12, color:"var(--wd-blue)", lineHeight:1.6 }}>
          Enter details as provided by the insurance company via email. These surveyors will appear in the directory for case assignment.
        </span>
      </div>

      <FSectionHeading>Surveyor Information</FSectionHeading>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <FField label="Full Name *">
          <input className="wd-input" placeholder="Full name" value={form.name} onChange={set("name")} autoFocus />
        </FField>
        <FField label="Phone *">
          <input className="wd-input" placeholder="Mobile number" value={form.phone} 
          onChange={(e) => {
          const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
          setForm(f => ({ ...f, phone: digits }));
        }}
        maxLength={10}
        inputMode="numeric" />
        </FField>
        <FField label="Email">
          <input className="wd-input" type="email" placeholder="Email address" value={form.email} onChange={set("email")} />
        </FField>
        <FField label="Insurance Company *">
          <input className="wd-input" placeholder="e.g. HDFC Ergo" value={form.insuranceCompany} onChange={set("insuranceCompany")} />
        </FField>
        <FField label="City">
          <input className="wd-input" placeholder="City" value={form.city} onChange={set("city")} />
        </FField>
        <FField label="State">
          <input className="wd-input" placeholder="State" value={form.state} onChange={set("state")} />
        </FField>
      </div>

      <FSectionHeading>License</FSectionHeading>
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <FField label="License No">
          <input className="wd-input" placeholder="License number" value={form.licenseNo} onChange={set("licenseNo")} />
        </FField>
        <FField label="License Expiry">
          <input className="wd-input" type="date" value={form.licenseExpiry} onChange={set("licenseExpiry")} />
        </FField>
      </div>

      <FSectionHeading>Specialization</FSectionHeading>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {SURVEY_TYPES.map(({ value, label }) => {
          const active = form.specialization.includes(value);
          return (
            <button key={value} className="wd-btn wd-btn-ghost wd-btn-sm"
              style={active ? { borderColor:"var(--wd-purple)", color:"var(--wd-purple)", background:"rgba(168,85,247,.12)" } : {}}
              onClick={() => toggleSpec(value)}>
              {active && "✓ "}{label}
            </button>
          );
        })}
      </div>
    </Modal>
  );
}
// ─── Assign to Case Modal ─────────────────────────────────────────────────────
function AssignModal({ sv, form, setForm, onClose, onAssign }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  if (!sv) return null;
  return (
    <Modal open={!!sv} onClose={onClose} title={`Assign ${sv.name} to Case`}
      footer={<>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="wd-btn wd-btn-primary"
          disabled={!form.caseId.trim() || !form.surveyType}
          onClick={onAssign}>
          Assign Surveyor
        </button>
      </>}
    >
      {/* Surveyor summary */}
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"12px 14px", background:"var(--wd-hover)", borderRadius:10 }}>
        <Avatar name={sv.name} size={40} />
        <div>
          <div style={{ fontWeight:600, fontSize:13 }}>{sv.name}</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)" }}>🏢 {sv.insuranceCompany} · 📍 {sv.city}</div>
          <div style={{ display:"flex", gap:5, marginTop:4 }}>
            {sv.specialization.map(s => (
              <span key={s} className="wd-badge wd-badge-purple" style={{ fontSize:9 }}>
                {SPEC_LABELS[s] || s}
              </span>
            ))}
          </div>
        </div>
      </div>

      <FField label="Case Number *">
        <input className="wd-input" placeholder="e.g. CPA-SIRTKNGG"
          value={form.caseId} onChange={set("caseId")} />
      </FField>
      <FField label="Survey Type *">
        <select className="wd-input wd-select" value={form.surveyType} onChange={set("surveyType")}>
          <option value="">Select survey type...</option>
          {SURVEY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
      </FField>
      <FField label="Scheduled Date & Time">
        <input className="wd-input" type="datetime-local"
          value={form.scheduledDate} onChange={set("scheduledDate")} />
      </FField>
      <FField label="Notes">
        <textarea className="wd-input" rows={2} style={{ resize:"vertical" }}
          placeholder="Any specific instructions for the surveyor..."
          value={form.notes} onChange={set("notes")} />
      </FField>

      <div style={{ padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)", fontSize:12, color:"var(--wd-blue)" }}>
        ℹ️ Case status will update to <strong>SPOT_SURVEY_ASSIGNED</strong> and the surveyor will be notified.
      </div>
    </Modal>
  );
}

// ─── Update Survey Status Modal ───────────────────────────────────────────────
// Admin updates based on info received from surveyor via email or direct communication
function UpdateSurveyModal({ survey, form, setForm, onClose, onSave }) {
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  if (!survey) return null;

  const isRejected   = form.status === "REJECTED";
  const isCompleted  = form.status === "COMPLETED";
  const isRescheduled= form.status === "RESCHEDULED";

  return (
    <Modal open={!!survey} onClose={onClose} title="Update Survey Status" maxWidth={520}
      footer={<>
        <button className="wd-btn wd-btn-ghost" onClick={onClose}>Cancel</button>
        <button className="wd-btn wd-btn-primary"
          disabled={!form.status || (isRejected && !form.rejectionReason.trim()) || (isCompleted && !form.surveyCompletedDate)}
          onClick={onSave}>
          Save Update
        </button>
      </>}
    >
      {/* Survey summary */}
      <div style={{ display:"flex", gap:12, padding:"12px 14px", background:"var(--wd-hover)", borderRadius:10 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", gap:8, alignItems:"center" }}>
            <span style={{ fontFamily:"var(--wd-mono)", fontSize:13, color:"var(--wd-blue)", fontWeight:700 }}>
              {survey.caseNo}
            </span>
            <span className="wd-badge wd-badge-purple" style={{ fontSize:10 }}>
              {SPEC_LABELS[survey.surveyType] || survey.surveyType}
            </span>
          </div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:3 }}>
            Surveyor: <strong style={{ color:"var(--wd-t2)" }}>{survey.surveyorName}</strong>
          </div>
          <div style={{ fontSize:11, color:"var(--wd-t3)", marginTop:2 }}>
            Assigned: {formatDateTime(survey.assignedAt)}
            {survey.scheduledDate && ` · Scheduled: ${formatDateTime(survey.scheduledDate)}`}
          </div>
        </div>
        <Badge variant={(SURVEY_STATUS_CFG[survey.surveyStatus] || {}).variant || "gray"}>
          {survey.surveyStatus}
        </Badge>
      </div>

      <FField label="Update Status *">
        <select className="wd-input wd-select" value={form.status} onChange={set("status")}>
          <option value="">Select new status...</option>
          <option value="ACCEPTED">Accepted — Surveyor confirmed the visit</option>
          <option value="RESCHEDULED">Rescheduled — New date required</option>
          <option value="REJECTED">Rejected — Surveyor declined</option>
          <option value="COMPLETED">Completed — Survey done</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
      </FField>

      {/* Rejection reason */}
      {isRejected && (
        <FField label="Rejection Reason *">
          <textarea className="wd-input" rows={3} style={{ resize:"vertical" }}
            placeholder="Enter the reason as communicated by the surveyor..."
            value={form.rejectionReason} onChange={set("rejectionReason")} />
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
            {[
              "Vehicle location not accessible",
              "Road blocked",
              "Incorrect address provided",
              "Vehicle moved before survey",
              "Schedule conflict",
            ].map(r => (
              <button key={r} className="wd-btn wd-btn-ghost wd-btn-sm"
                style={form.rejectionReason === r ? { borderColor:"var(--wd-red)", color:"var(--wd-red)" } : {}}
                onClick={() => setForm(f => ({ ...f, rejectionReason:r }))}>
                {r}
              </button>
            ))}
          </div>
        </FField>
      )}

      {/* Completion date — required when completed */}
      {isCompleted && (
        <FField label="Survey Completed Date & Time *">
          <input className="wd-input" type="datetime-local"
            value={form.surveyCompletedDate} onChange={set("surveyCompletedDate")} />
        </FField>
      )}

      {/* Remarks — for completed or rescheduled */}
      {(isCompleted || isRescheduled) && (
        <FField label={isCompleted ? "Survey Findings / Remarks" : "Reschedule Reason"}>
          <textarea className="wd-input" rows={3} style={{ resize:"vertical" }}
            placeholder={isCompleted
              ? "Enter surveyor's findings as received via email or communication..."
              : "Reason for rescheduling..."}
            value={form.remarks} onChange={set("remarks")} />
        </FField>
      )}

      <div style={{ padding:"10px 14px", background:"rgba(59,130,246,.07)", borderRadius:10, border:"1px solid rgba(59,130,246,.15)", fontSize:12, color:"var(--wd-blue)", lineHeight:1.6 }}>
        ℹ️ Admin updates this based on information received from the surveyor via email or direct communication.
        {isRejected && <span style={{ color:"var(--wd-red)" }}> A new surveyor will need to be assigned to this case.</span>}
        {isCompleted && " Case status will advance to the next workflow stage."}
      </div>
    </Modal>
  );
}

// ─── Surveyor Detail Modal ────────────────────────────────────────────────────
function SurveyorDetailModal({ sv, surveys, onClose, onAssign, onNavigate }) {
  if (!sv) return null;

  const licExpiring  = new Date(sv.licenseExpiry) < new Date(Date.now() + 90*86400000);
  const svSurveys    = surveys.filter(s => s.surveyorId === sv.id);
  const active       = svSurveys.filter(s => ["ASSIGNED","ACCEPTED","RESCHEDULED"].includes(s.surveyStatus));
  const completed    = svSurveys.filter(s => s.surveyStatus === "COMPLETED");
  const rejected     = svSurveys.filter(s => s.surveyStatus === "REJECTED");

  return (
    <Modal open={!!sv} onClose={onClose} title="Surveyor Profile" maxWidth={600}
      footer={
        <div style={{ display:"flex", justifyContent:"space-between", width:"100%", alignItems:"center" }}>
          <button className="wd-btn wd-btn-ghost" onClick={onClose}>Close</button>
          <button className="wd-btn wd-btn-primary" onClick={onAssign}>Assign to Case</button>
        </div>
      }
    >
      {/* Identity */}
      <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
        <Avatar name={sv.name} size={52} />
        <div style={{ flex:1 }}>
          <div style={{ fontWeight:700, fontSize:16, color:"var(--wd-t1)" }}>{sv.name}</div>
          <div style={{ fontSize:12, color:"var(--wd-t3)", marginTop:3 }}>
            🏢 {sv.insuranceCompany} · 📍 {sv.city}, {sv.state}
          </div>
          <div style={{ display:"flex", gap:5, marginTop:6 }}>
            {sv.specialization.map(s => (
              <span key={s} className="wd-badge wd-badge-purple" style={{ fontSize:10 }}>
                {SPEC_LABELS[s] || s}
              </span>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", gap:16, textAlign:"center" }}>
          {[
            { label:"Total",     value:sv.totalSurveys, color:"var(--wd-t1)"    },
            { label:"Active",    value:active.length,   color:"var(--wd-orange)"},
            { label:"Completed", value:completed.length,color:"var(--wd-green)" },
            { label:"Rejected",  value:rejected.length, color:"var(--wd-red)"   },
          ].map(({ label, value, color }) => (
            <div key={label}>
              <div style={{ fontSize:20, fontWeight:800, color }}>{value}</div>
              <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600 }}>{label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="wd-divider" />

      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
        <InfoRow label="Phone"          value={sv.phone} mono />
        <InfoRow label="Email"          value={sv.email} />
        <InfoRow label="License No"     value={sv.licenseNo} mono />
        <InfoRow label="License Expiry" value={formatDate(sv.licenseExpiry)} />
        <InfoRow label="Insurance Co"   value={sv.insuranceCompany} />
        <InfoRow label="Joined"         value={formatDate(sv.joinedAt)} />
      </div>

      {licExpiring && (
        <div style={{ display:"flex", gap:10, padding:"10px 14px", background:"rgba(239,68,68,.08)", borderRadius:10, border:"1px solid rgba(239,68,68,.2)" }}>
          <span>⚠️</span>
          <span style={{ fontSize:12, color:"var(--wd-red)" }}>
            License expires on <strong>{formatDate(sv.licenseExpiry)}</strong>. Please inform the insurance company to renew.
          </span>
        </div>
      )}

      {/* Active surveys for this surveyor */}
      {active.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".05em" }}>
            Active Surveys ({active.length})
          </div>
          {active.map(s => (
            <div key={s.id} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", background:"var(--wd-hover)", borderRadius:8 }}>
              <div style={{ display:"flex", gap:10, alignItems:"center" }}>
                <span style={{ fontFamily:"var(--wd-mono)", fontSize:12, color:"var(--wd-blue)", fontWeight:600 }}>{s.caseNo}</span>
                <span className="wd-badge wd-badge-purple" style={{ fontSize:9 }}>{SPEC_LABELS[s.surveyType] || s.surveyType}</span>
                <Badge variant={(SURVEY_STATUS_CFG[s.surveyStatus]||{}).variant||"gray"} style={{ fontSize:10 }}>{s.surveyStatus}</Badge>
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                <span style={{ fontSize:11, color:"var(--wd-t3)" }}>
                  {s.scheduledDate ? `Sched: ${formatDate(s.scheduledDate)}` : `Assigned: ${formatDate(s.assignedAt)}`}
                </span>
                <button className="wd-btn wd-btn-ghost wd-btn-sm"
                  onClick={() => { onClose(); onNavigate(`/admin/cases/${s.caseNo}`); }}>
                  View →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────
function MiniStat({ label, value, color = "var(--wd-t1)" }) {
  return (
    <div>
      <div style={{ fontSize:10, color:"var(--wd-t3)", textTransform:"uppercase", fontWeight:600, letterSpacing:".05em" }}>{label}</div>
      <div style={{ fontSize:14, fontWeight:700, color, marginTop:2 }}>{value}</div>
    </div>
  );
}

function FSectionHeading({ children }) {
  return (
    <div style={{ fontSize:11, fontWeight:700, color:"var(--wd-t3)", textTransform:"uppercase", letterSpacing:".06em", paddingBottom:3, borderBottom:"1px solid var(--wd-border)" }}>
      {children}
    </div>
  );
}

function FField({ label, children }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
      <label className="wd-form-label">{label}</label>
      {children}
    </div>
  );
}

function emptyAddForm() {
  return { name:"", phone:"", email:"", insuranceCompany:"", city:"", state:"", licenseNo:"", licenseExpiry:"", specialization:[] };
}

